import React, { Component } from 'react';
import _ from 'lodash';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import { Link, Redirect } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import scrollToElement from 'scroll-to-element';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { ScrollMenu } from 'react-horizontal-scrolling-menu';

import actions from 'api/actions';
import Calendar from 'components/shared/calendar-with-default-styles';
import CustomizableGoogleMap from 'components/shared/customizable-google-map';
import Card from 'components/shared/card';
import { IconList, IconListItem } from 'components/shared/landing/icon-list';
import LearningCenter from 'components/shared/landing/learning-center';
import HorizontalCarousel from 'components/shared/horizontal-carousel';
import PlanCard from 'components/shared/plan-card';
import OptionChoiceCard from 'components/shared/plan-card/option-choice-card';
import LearningCenterContactModal from './contact-modal';
import PoweredByStripe from 'assets/img/powered_by_stripe.png';
import OnlineLocation from 'assets/img/online-location-students.jpg';
import AtHomeLocation from 'assets/img/olc-elligible-students.jpg';
import TimezoneIndicator from 'components/shared/timezone-indicator';
import styles from './styles.module.scss';

const moment = extendMoment(Moment);

const locations = {
  'ChIJmTYBS8632YgRAd8n6kWtG-M': 'Coconut Grove Branch Library',
  'ChIJRXhIT-622YgRSXAsv-NSY5U': 'Hispanic Branch Library',
  ChIJ30tOQ4232YgRlejomb_1d4A: 'Coral Gables Branch Library',
};

const hasActiveOpeningsFor = (availabilities, date) => {
  return activeOpeningsFor(availabilities, date).length > 0;
};

const activeOpeningsFor = (availabilities, date) => {
  const momentDate = moment(date);
  return availabilities.filter(
    availability =>
      availability.openings.filter(opening => {
        if (opening.date) {
          return (
            opening.times.length > 0 &&
            moment
              .utc(
                `${opening.date} ${opening.times[0][0]}`,
                'YYYY-MM-DD HH:mm:ss',
              )
              .local()
              .isSame(momentDate, 'day') &&
            opening.times.filter(timeSlot =>
              moment
                .utc(`${opening.date} ${timeSlot[0]}`, 'YYYY-MM-DD HH:mm:ss')
                .clone()
                .add(1, 'hour')
                .isAfter(moment()),
            ).length > 0
          );
        }

        return momentDate.format('dddd').toLowerCase() === opening.day;
      }).length > 0,
  );
};

const hasSchedulesFor = (schedules, date) => {
  return schedulesFor(schedules, date).length > 0;
};

const schedulesFor = (schedules, date) => {
  const momentDate = moment(date);
  return schedules.filter(
    schedule =>
      (moment(schedule.start_date).isBefore(momentDate) ||
        moment(schedule.start_date).isSame(momentDate)) &&
      (moment(schedule.end_date).isAfter(momentDate) ||
        moment(schedule.end_date).isSame(momentDate)) &&
      _.includes(schedule.days, momentDate.isoWeekday()),
  );
};

function locationHeaderText({ locations, online, atHome }) {
  const textArray = [];
  if (online) {
    textArray.push('Online');
  }
  if (atHome) {
    textArray.push('At-home');
  }
  if (locations.length > 0) {
    textArray.push('In-person');
  }

  if (textArray.length === 1) return textArray[0];
  if (textArray.length === 2) return textArray.join(' & ');
  if (textArray.length === 3) return textArray.join(', ');
}

function getAvailableLocations(availabilities) {
  return _.chain(availabilities)
    .flatMap('openings')
    .reduce(
      (acc, opening) => {
        if (opening.location) {
          acc.locations.push(opening.location);
          acc.locations = _.uniq(acc.locations);
        }
        if (opening.online) {
          acc.online = true;
        }
        if (opening.at_home) {
          acc.atHome = true;
        }
        return acc;
      },
      { online: false, atHome: false, locations: [] },
    )
    .value();
}

function getMapCarouselOffset(availableLocations) {
  if (availableLocations.online && availableLocations.atHome) return 2;
  if (availableLocations.online || availableLocations.atHome) return 1;
  return 0;
}

function classifyTile(
  availabilities,
  schedules,
  selectedDate = null,
  { date },
) {
  if (selectedDate && moment(date).isSame(moment(selectedDate))) {
    return styles.selectedDate;
  }
  if (hasActiveOpeningsFor(availabilities, date)) {
    return styles.availableDate;
  }
  if (hasSchedulesFor(schedules, date)) {
    return styles.availableDate;
  }
  return styles.inactiveDate;
}

function addSvgSlash(availabilities, schedules) {
  return ({ activeStartDate, date, view }) => {
    const disabled =
      !hasActiveOpeningsFor(availabilities, date) &&
      !hasSchedulesFor(schedules, date);
    if (disabled)
      return (
        <svg width="25" height="25" viewBox="0 0 25 25">
          <path d="M 0 25 L 25 0" stroke="#808080" strokeWidth="1" />
        </svg>
      );
    return null;
  };
}

function dayToInt(day) {
  const days = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];
  return days.indexOf(day) > -1 ? days.indexOf(day) : false;
}

function getTimeLocationsFromAvailabilities(availabilities, selectedDate) {
  const startCap = moment().startOf('month');
  const endCap = moment().add(1, 'month').endOf('month');
  const range = moment.range(startCap, endCap);
  const selectedDay = moment(selectedDate).day();
  const selectedMoment = moment(selectedDate);
  return (
    _.chain(availabilities)
      .compact()
      .reject(
        availability => !availability.times || availability.times.length === 0,
      )
      .filter(
        availability =>
          moment
            .utc(
              `${availability.date} ${availability.times[0][0]}`,
              'YYYY-MM-DD HH:mm:ss',
            )
            .local()
            .isSame(selectedMoment, 'day') ||
          dayToInt(availability.day) === selectedDay,
      )
      // .tap(availabilities => console.log('after first filter', availabilities))
      .filter(
        (availability, index, collection) =>
          availability.date ||
          (availability.day && !_.find(collection, 'date')),
      )
      // .tap(availabilities => console.log('after second filter', availabilities))
      .flatMap(availability => {
        if (availability.date) return availability;
        if (availability.day) {
          return _.chain(Array.from(range.by('day')))
            .filter(date => date.day() === selectedDay)
            .map(date => ({
              ...availability,
              date: date.format('YYYY-MM-DD'),
            }))
            .value();
        }
        return null;
      })
      // .tap(availabilities => console.log('after first flatmap', availabilities))
      .map(availability => {
        const timeLocation = {
          id: availability.id,
          date: availability.date,
          times: availability.times,
          locations: [],
        };
        if (availability.online) {
          timeLocation.locations.push('Online');
        }
        if (availability.at_home) {
          timeLocation.locations.push('At Home');
        }
        if (availability.location) {
          timeLocation.locations.push(locations[availability.location]);
        }
        return timeLocation;
      })
      // .tap(availabilities => console.log('after first map', availabilities))
      .flatMap(availability => {
        return _.flatMap(availability.times, timeRange => {
          let endTime;
          const startTime = moment
            .utc(`${availability.date} ${timeRange[0]}`)
            .local();
          const naiveEnd = moment
            .utc(`${availability.date} ${timeRange[timeRange.length - 1]}`)
            .local();
          if (startTime.isAfter(naiveEnd)) {
            endTime = naiveEnd.clone().add(1, 'day');
          } else {
            endTime = naiveEnd.clone();
          }
          const range = moment.range(startTime, endTime);

          return Array.from(range.by('minute', { step: 60 }))
            .filter(momentDate =>
              momentDate.clone().add(60, 'minutes').isSameOrBefore(endTime),
            )
            .map(momentDate => ({
              id: availability.id,
              date: momentDate.format('YYYY-MM-DD'),
              start: momentDate.format('h:mma'),
              end: momentDate.clone().add(60, 'minutes').format('h:mma'),
              locations: availability.locations,
            }));
        });
      })
      // .tap(availabilities => console.log('after second flatmap', availabilities))
      .reduce((acc, availability, key, availabilities) => {
        const previous = _.find(
          acc,
          _.pick(availability, ['date', 'start', 'end']),
        );
        if (previous) {
          previous.locations = _.uniq([
            ...previous.locations,
            ...availability.locations,
          ]);
          return acc;
        }
        acc.push(availability);
        return acc;
      }, [])
      .uniqBy(
        availability =>
          `${availability.date} ${availability.start} ${availability.end}`,
      )
      .sortBy('date')
      .value()
  );
}

const Arrow = ({ icon, className }) => {
  return (
    <div className={className}>
      <FontAwesomeIcon icon={icon} />
    </div>
  );
};

const ArrowLeft = Arrow({ icon: 'chevron-left', className: styles.arrowPrev });
const ArrowRight = Arrow({
  icon: 'chevron-right',
  className: styles.arrowNext,
});

class LearningCenterDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      availabilities: [],
      services: [],
      schedules: [],
      selectedPlanId: null,
      selectedDate: null,
      selectedTime: null,
      selectedLocation: null,
      learningCenterCarousel: 0,
      mapCarousel: 0,
      dateSelectModalOpen: false,
      contactModalOpen: false,
      optionsModalOpen: false,
    };

    this.dismissModal = this.dismissModal.bind(this);
    this.onLearningCenterCarouselPrev =
      this.onLearningCenterCarouselPrev.bind(this);
    this.onLearningCenterCarouselNext =
      this.onLearningCenterCarouselNext.bind(this);
    this.onMapCarouselPrev = this.onMapCarouselPrev.bind(this);
    this.onMapCarouselNext = this.onMapCarouselNext.bind(this);
    this.onClickMapCarouselIcon = this.onClickMapCarouselIcon.bind(this);
    this.onOpenContactModal = this.onOpenContactModal.bind(this);
    this.onDismissContactModal = this.onDismissContactModal.bind(this);
    this.startPlanPurchase = this.startPlanPurchase.bind(this);
    this.onOptionsModalToggle = this.onOptionsModalToggle.bind(this);
    this.onSelectScheduleOnboarding =
      this.onSelectScheduleOnboarding.bind(this);
    this.onSelectPurchasePlan = this.onSelectPurchasePlan.bind(this);
    this.onDateSelectModalOpen = this.onDateSelectModalOpen.bind(this);
    this.onDateSelectModalClose = this.onDateSelectModalClose.bind(this);
  }

  componentDidMount() {
    this.fetchCenterData();
  }

  componentDidUpdate({ learningCenterId: prevLearningCenterId }) {
    const { learningCenterId } = this.props;

    if (learningCenterId !== prevLearningCenterId) {
      this.fetchCenterData();
    }
  }

  /**
   * Get the necessary data for displaying this center, including its openings, services, and schedules.
   */
  fetchCenterData() {
    const { learningCenterId, history } = this.props;
    const openingsRequest = actions
      .rawGet(`learning_centers/${learningCenterId}/openings`)
      .then(r => this.setState({ availabilities: r.data }));
    const servicesRequest = actions.get('plans').then(r => {
      const services = _.filter(
        r.data,
        plan =>
          _.includes(
            plan.learning_center_ids,
            parseInt(learningCenterId, 10),
          ) || plan.learning_center_ids.length === 0,
      );
      this.setState({ services });
    });
    const schedulesRequest = actions
      .rawGet(`learning_centers/${learningCenterId}/schedules`)
      .then(r => this.setState({ schedules: r.data }));

    Promise.all([openingsRequest, servicesRequest, schedulesRequest]).catch(
      e => {
        Sentry.captureException(e);
        history.push('/marketplace');
      },
    );
  }

  onLearningCenterCarouselPrev() {
    this.setState(prevState => {
      const { learningCenterId, allCenters } = this.props;
      const { learningCenterCarousel } = prevState;
      const otherCenters = _.reject(allCenters, [
        'id',
        parseInt(learningCenterId, 10),
      ]);
      return {
        learningCenterCarousel:
          learningCenterCarousel === 0
            ? otherCenters.length - 1
            : learningCenterCarousel - 1,
      };
    });
  }

  onLearningCenterCarouselNext() {
    this.setState(prevState => {
      const { learningCenterId, allCenters } = this.props;
      const { learningCenterCarousel } = prevState;
      const otherCenters = _.reject(allCenters, [
        'id',
        parseInt(learningCenterId, 10),
      ]);
      return {
        learningCenterCarousel:
          learningCenterCarousel === otherCenters.length - 1
            ? 0
            : learningCenterCarousel + 1,
      };
    });
  }

  onMapCarouselPrev() {
    this.setState(prevState => {
      const { availabilities, mapCarousel } = prevState;
      const availableLocations = getAvailableLocations(availabilities);
      const mapCarouselOffset = getMapCarouselOffset(availableLocations);
      return {
        mapCarousel:
          mapCarousel === 0
            ? availableLocations.locations.length + mapCarouselOffset - 1
            : mapCarousel - 1,
      };
    });
  }

  onMapCarouselNext() {
    this.setState(prevState => {
      const { availabilities, mapCarousel } = prevState;
      const availableLocations = getAvailableLocations(availabilities);
      const mapCarouselOffset = getMapCarouselOffset(availableLocations);
      return {
        mapCarousel:
          mapCarousel ===
          availableLocations.locations.length + mapCarouselOffset - 1
            ? 0
            : mapCarousel + 1,
      };
    });
  }

  onClickMapCarouselIcon(mapCarousel) {
    this.setState({ mapCarousel });
  }

  dismissModal() {
    this.setState({ dateSelectModalOpen: false });
  }

  onOpenContactModal() {
    this.setState({ contactModalOpen: true, dateSelectModal: false });
  }

  onDismissContactModal() {
    this.setState({ contactModalOpen: false });
  }

  startPlanPurchase(serviceId) {
    this.setState({ selectedPlanId: serviceId });
  }

  onOptionsModalToggle() {
    this.setState(prevState => ({
      optionsModalOpen: !prevState.optionsModalOpen,
    }));
  }

  onSelectScheduleOnboarding() {
    this.setState({ optionsModalOpen: false }, () => {
      scrollToElement('#schedule');
    });
  }

  onSelectPurchasePlan() {
    this.setState({ optionsModalOpen: false }, () => {
      scrollToElement('#plans');
    });
  }

  onDateSelectModalOpen(selectedDate) {
    this.setState({ dateSelectModalOpen: selectedDate });
  }

  onDateSelectModalClose() {
    this.setState({ dateSelectModalOpen: false });
  }

  render() {
    const {
      services,
      selectedPlanId,
      selectedSlot,
      availabilities,
      schedules,
      learningCenterCarousel,
      mapCarousel,
      dateSelectModalOpen,
      contactModalOpen,
      optionsModalOpen,
    } = this.state;
    const { learningCenterId, allCenters } = this.props;
    const learningCenter = _.find(allCenters, [
      'id',
      parseInt(learningCenterId, 10),
    ]);

    if (!availabilities || !learningCenter || !services) {
      return <div />;
    }

    let dateModalAvailabilities, dateModalSchedules;

    const startOfMonth = moment().startOf('month');
    const startOfNextMonth = moment().add(1, 'month').startOf('month');

    const otherCenters = _.reject(allCenters, [
      'id',
      parseInt(learningCenterId, 10),
    ]);

    const availableLocations = getAvailableLocations(availabilities);
    const mapCarouselOffset = getMapCarouselOffset(availableLocations);
    const openings = _.flatMap(availabilities, 'openings');
    const selectedPlan = selectedPlanId
      ? _.find(services, ['id', selectedPlanId])
      : null;

    if (dateSelectModalOpen) {
      dateModalAvailabilities = getTimeLocationsFromAvailabilities(
        openings,
        moment(dateSelectModalOpen).format('YYYY-MM-DD'),
      );
      dateModalSchedules = schedulesFor(schedules, dateSelectModalOpen);
    }

    return (
      <div className={styles.learningCenterDetails}>
        {selectedPlan && selectedPlan.options.length === 0 && (
          <Redirect
            to={{
              pathname: '/marketplace/checkout',
              state: { learningCenter, service: selectedPlan },
            }}
          />
        )}
        {selectedPlan && selectedPlan.options.length > 0 && (
          <Redirect
            to={{
              pathname: `/services/${selectedPlan.id}/${learningCenter.id}`,
            }}
          />
        )}
        {selectedSlot && (
          <Redirect
            to={{
              pathname: `/learning-center-onboard`,
              state: { selectedSlot, learningCenter },
            }}
          />
        )}
        <section className={styles.header}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.image}>
                <img src={learningCenter.image_file_name} />
              </div>
              <div className={styles.details}>
                <h1>{learningCenter.title}</h1>
                <h2>{learningCenter.location}</h2>
                <h3>{locationHeaderText(availableLocations)}</h3>
                <h4>
                  <FontAwesomeIcon icon="award" className="text-orange" />{' '}
                  Unyted Certified Educator
                </h4>
                <IconList>
                  {learningCenter.subjects.map(subject => (
                    <IconListItem
                      key={subject}
                      icon="check"
                      iconClassName="text-apple"
                    >
                      <span className={styles.subject}>{subject}</span>
                    </IconListItem>
                  ))}
                </IconList>
              </div>
            </div>
          </div>
        </section>
        <section id="plans" className={styles.plans}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className="col-12">
                <h2>Services Available</h2>
                <IconList>
                  <IconListItem icon="check" iconClassName="text-apple">
                    Find a perfect fit
                  </IconListItem>
                  <IconListItem icon="check" iconClassName="text-apple">
                    Access to Parent App
                  </IconListItem>
                  <IconListItem icon="check" iconClassName="text-apple">
                    Contact us for support
                  </IconListItem>
                </IconList>
              </div>
            </div>
          </div>
          <div className={styles.container}>
            <div className={styles.row}>
              {services.length > 4 ? (
                <div className="col-12 py-3">
                  <ScrollMenu
                    data={services.map(service => (
                      <PlanCard
                        key={service.id}
                        plan={service}
                        detailsButtonType="link"
                        linkLearningCenter={learningCenterId}
                        onCtaClick={_.partial(
                          this.startPlanPurchase,
                          service.id,
                        )}
                        linkPrefix="/services"
                      />
                    ))}
                    arrowLeft={ArrowLeft}
                    arrowRight={ArrowRight}
                  />
                </div>
              ) : (
                <div className="d-flex flex-row flex-wrap justify-content-around">
                  {services.map(service => (
                    <PlanCard
                      key={service.id}
                      plan={service}
                      detailsButtonType="link"
                      linkLearningCenter={learningCenterId}
                      onCtaClick={_.partial(this.startPlanPurchase, service.id)}
                      linkPrefix="/services"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
        <section className={styles.aboutInstructor}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.tldrColumn}>
                <h2>About {learningCenter.instructor_first_name}</h2>
                <img
                  className={styles.roundImage}
                  src={learningCenter.image_file_name}
                />
                {learningCenter.instructor_credentials.slice(0, 2).map(cred => (
                  <div key={cred} className={styles.credential}>
                    {cred}
                  </div>
                ))}
              </div>
              <div className={styles.mainColumn}>
                <p className={styles.instructorDescription}>
                  {learningCenter.instructor_description}
                </p>
                <blockquote className={styles.instructorQuote}>
                  {learningCenter.instructor_quote}
                </blockquote>
                <div className={styles.contactRow}>
                  <button
                    type="button"
                    onClick={this.onOpenContactModal}
                    className={styles.contactInstructor}
                  >
                    Contact {learningCenter.instructor_first_name}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section id="schedule" className={styles.availability}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className="col-12">
                <h2>Dates Available</h2>
                <p className={styles.subtext}>
                  Click a date for times and special schedules.
                </p>
                <TimezoneIndicator />
              </div>
              <div className={styles.calendar}>
                <Calendar
                  calendarType="US"
                  onClickDay={this.onDateSelectModalOpen}
                  defaultActiveStartDate={startOfMonth.toDate()}
                  defaultView="month"
                  maxDetail="month"
                  minDetail="month"
                  minDate={moment().toDate()}
                  maxDate={moment().endOf('month').toDate()}
                  next2label={null}
                  prev2label={null}
                  showNeighboringMonth={false}
                  tileDisabled={({ activeStartDate, date }) =>
                    !hasActiveOpeningsFor(availabilities, date) &&
                    !hasSchedulesFor(schedules, date)
                  }
                  tileClassName={_.partial(
                    classifyTile,
                    availabilities,
                    schedules,
                    null,
                  )}
                  tileContent={addSvgSlash(availabilities, schedules)}
                />
              </div>
              <div className={styles.calendar}>
                <Calendar
                  calendarType="US"
                  onClickDay={this.onDateSelectModalOpen}
                  defaultActiveStartDate={startOfNextMonth.toDate()}
                  defaultView="month"
                  maxDetail="month"
                  minDetail="month"
                  minDate={startOfNextMonth.toDate()}
                  maxDate={startOfNextMonth.clone().add(1, 'month').toDate()}
                  next2label={null}
                  prev2label={null}
                  showNeighboringMonth={false}
                  tileDisabled={({ activeStartDate, date }) =>
                    !hasActiveOpeningsFor(availabilities, date) &&
                    !hasSchedulesFor(schedules, date)
                  }
                  tileClassName={_.partial(
                    classifyTile,
                    availabilities,
                    schedules,
                    null,
                  )}
                  tileContent={addSvgSlash(availabilities, schedules)}
                />
              </div>
            </div>
          </div>
        </section>
        <section className={styles.whatLearn}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.tldrColumn}>
                <h2>What Students Learn Here</h2>
                <IconList>
                  {learningCenter.subjects.map(subject => (
                    <IconListItem
                      key={subject}
                      icon="check"
                      iconClassName="text-apple"
                    >
                      <span className={styles.subject}>{subject}</span>
                    </IconListItem>
                  ))}
                </IconList>
              </div>
              <div className={styles.mainColumn}>
                <p className={styles.description}>
                  {learningCenter.description}
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.usingUnyted}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.tldrColumn}>
                <h2>Benefits of The TEAM Method&trade;</h2>
                <div className={styles.benefitsList}>
                  <IconList>
                    <IconListItem icon="check" iconClassName="text-apple">
                      Deeper Learning
                    </IconListItem>
                    <IconListItem icon="check" iconClassName="text-apple">
                      Power Skills put into practice
                    </IconListItem>
                    <IconListItem icon="check" iconClassName="text-apple">
                      Better Problem-solving
                    </IconListItem>
                    <IconListItem icon="check" iconClassName="text-apple">
                      Easier to tutor and coach at same time
                    </IconListItem>
                  </IconList>
                </div>
              </div>
              <div className={styles.mainColumn}>
                <p className={styles.aboutUnyted}>
                  <Link to="/the-team-method">The TEAM Method</Link> is used by
                  educators to build deeper academic skills and SEL Power Skills
                  at the same time. The patent-pending technology makes it
                  easier for educators to coach students in two ways: (1)
                  understand academic topics faster and (2) connect it to real
                  world skills to make it all relevant. More than just
                  individual worksheet-type (digital or otherwise) problems
                  where students mostly aim to memorize, our educators use The
                  TEAM Method to elevate students to higher order thinking and
                  creativity (making games!).
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.whatKind}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.tldrColumn}>
                <h2>Types of Learning</h2>
              </div>
              <div className={styles.mainColumn}>
                <div className={styles.row}>
                  <div className={styles.learningType}>
                    <h3>Group Sessions</h3>
                    <IconList>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Group problem-solve with peers
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Real world Power Skills practice
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        More cost savings per student
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Continued access to digital platform
                      </IconListItem>
                    </IconList>
                  </div>
                  <div className={styles.learningType}>
                    <h3>1-to-1 Sessions</h3>
                    <IconList>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Group problem-solve with coach
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Real world Power Skills practice
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Highest level of personalization
                      </IconListItem>
                      <IconListItem icon="check" iconClassName="text-apple">
                        Continued access to digital platform
                      </IconListItem>
                    </IconList>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.where}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className="col-12">
                <h2>Where learning takes place</h2>
              </div>
            </div>
          </div>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.mapCarousel}>
                <div className={styles.icons}>
                  {availableLocations.online && (
                    <div className={styles.locationKey}>
                      <FontAwesomeIcon
                        icon="laptop"
                        className={
                          mapCarousel === 0
                            ? styles.selected
                            : styles.unselected
                        }
                        onClick={_.partial(this.onClickMapCarouselIcon, 0)}
                      />
                      <p>Online</p>
                    </div>
                  )}
                  {availableLocations.atHome && (
                    <div className={styles.locationKey}>
                      <FontAwesomeIcon
                        icon="home"
                        className={
                          (availableLocations.online && mapCarousel === 1) ||
                          (!availableLocations.online && mapCarousel === 0)
                            ? styles.selected
                            : styles.unselected
                        }
                        onClick={_.partial(this.onClickMapCarouselIcon, 1)}
                      />
                      <p>In-home</p>
                    </div>
                  )}
                  {availableLocations.locations.map((location, index) => (
                    <div className={styles.locationKey} key={location}>
                      <FontAwesomeIcon
                        icon="map-marker-alt"
                        className={
                          mapCarousel === index + mapCarouselOffset
                            ? styles.selected
                            : styles.unselected
                        }
                        onClick={_.partial(
                          this.onClickMapCarouselIcon,
                          index + mapCarouselOffset,
                        )}
                      />
                      <p>
                        Physical
                        <br />
                        Location
                      </p>
                    </div>
                  ))}
                </div>
                <HorizontalCarousel
                  current={mapCarousel}
                  className={styles.innerCarousel}
                  onClickPrev={this.onMapCarouselPrev}
                  onClickNext={this.onMapCarouselNext}
                  numDisplayItems={1}
                  leftIconClassName={styles.carouselIcon}
                  rightIconClassName={styles.carouselIcon}
                  itemWrapperClass={styles.carouselItem}
                >
                  {availableLocations.online && (
                    <div className={styles.online}>
                      <h3>Sessions can be entirely online</h3>
                      <img src={OnlineLocation} />
                    </div>
                  )}
                  {availableLocations.atHome && (
                    <div className={styles.atHome}>
                      <h3>Sessions can be at your home!</h3>
                      <img src={AtHomeLocation} />
                    </div>
                  )}
                  {availableLocations.locations.map(location => (
                    <div key={location} className={styles.map}>
                      <CustomizableGoogleMap placeId={location} />
                    </div>
                  ))}
                </HorizontalCarousel>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.reminders}>
          <div className={styles.container}>
            <div className={styles.row}>
              <div className={styles.tldrColumn}>
                <h2>Reminders</h2>
              </div>
              <div className={styles.mainColumn}>
                <h3>Device &amp; Internet</h3>
                <p>
                  Since instruction takes place primarily using{' '}
                  <Link to="/online-platform">Unyted's Online Platform</Link>,
                  learners will need some sort of device capable of runnning a
                  modern web browser and an internet connection. You can use a
                  phone, tablet, or laptop!
                </p>
                <h3>First session</h3>
                <p>
                  Each learner may be given an onboarding session to meet
                  educator and get introduced to TEAM Method. These{' '}
                  <strong>may</strong> be included in educators' programs. For 1
                  on 1 sessions, they'll need to be scheduled by the parent.
                </p>
                <h3>
                  Secure Payments
                  <a href="https://stripe.com" target="_blank" rel="noreferrer">
                    <img
                      className="ml-3"
                      src={PoweredByStripe}
                      alt="Powered by Stripe"
                    />
                  </a>
                </h3>
                <p>
                  All payments are processed by our partner, Stripe. Stripe is a
                  secure payment platform using the same industry-standard
                  security and encryption you're used to!
                </p>
              </div>
            </div>
          </div>
        </section>
        {otherCenters.length > 0 && (
          <section className={styles.explore}>
            <div className={styles.container}>
              <div className={styles.row}>
                <h2 className={styles.exploreHeader}>
                  Explore other Unyted Learning Centers
                </h2>
                <div className={styles.otherCenter}>
                  <HorizontalCarousel
                    current={learningCenterCarousel}
                    onClickPrev={this.onLearningCenterCarouselPrev}
                    onClickNext={this.onLearningCenterCarouselNext}
                    numDisplayItems={1}
                    leftIconClassName={styles.carouselIcon}
                    rightIconClassName={styles.carouselIcon}
                    itemWrapperClass={styles.otherCenterWrapper}
                  >
                    {otherCenters.map(center => (
                      <LearningCenter
                        key={center.id}
                        center={center}
                        id={center.id}
                        name={center.instructor_name}
                        credentials={center.instructor_credentials}
                        title={center.title}
                        leaderPicture={center.image_file_name}
                        professionalTitle={center.professionalTitle}
                        description={center.description}
                        location={center.location}
                        subjects={center.subjects}
                        capacityMessage={center.capacityMessage}
                        linkPrefix="/unyted-learning-centers"
                      />
                    ))}
                  </HorizontalCarousel>
                </div>
              </div>
            </div>
          </section>
        )}
        <div className={styles.stickyFloater}>
          <div className={styles.stickyLeft}>
            <img
              className={styles.instructorImage}
              src={learningCenter.image_file_name}
            />
            <h3>{learningCenter.title}</h3>
          </div>
          <div className={styles.stickyRight}>
            <button onClick={this.onOptionsModalToggle} type="button">
              See Options
            </button>
          </div>
        </div>
        <LearningCenterContactModal
          open={contactModalOpen}
          dismiss={this.onDismissContactModal}
          learningCenterId={learningCenterId}
          instructorName={learningCenter.instructor_first_name}
        />
        <Modal
          size="lg"
          isOpen={optionsModalOpen}
          toggle={this.onOptionsModalToggle}
        >
          <ModalHeader toggle={this.onOptionsModalToggle}>
            <strong>How do you want to start?</strong>
          </ModalHeader>
          <ModalBody>
            <div className={styles.container}>
              <div className={`${styles.row} equal-height`}>
                <div className="col-lg-6 mb-3 mb-lg-0">
                  <Card
                    cardClass={styles.optionCard}
                    onClick={this.onSelectScheduleOnboarding}
                  >
                    <FontAwesomeIcon
                      icon="calendar-alt"
                      size="4x"
                      className="text-apple"
                    />
                    <h4>Schedule First Session</h4>
                    <span>
                      Schedule your child's onboarding session now and purchase
                      a service.
                    </span>
                  </Card>
                </div>
                <div className="col-lg-6">
                  <Card
                    cardClass={styles.optionCard}
                    onClick={this.onSelectPurchasePlan}
                  >
                    <FontAwesomeIcon
                      icon={['far', 'store']}
                      size="4x"
                      className="text-orange"
                    />
                    <h4>View Services</h4>
                    <span>
                      Purchase a service now, schedule your child's first
                      session later.
                    </span>
                  </Card>
                </div>
              </div>
            </div>
          </ModalBody>
        </Modal>
        <Modal
          size="lg"
          isOpen={!!dateSelectModalOpen}
          toggle={this.onDateSelectModalClose}
        >
          <ModalHeader toggle={this.onDateSelectModalClose}>
            {moment(dateSelectModalOpen).format('dddd, MMM Do, YYYY')}
          </ModalHeader>
          <ModalBody>
            {dateModalSchedules && dateModalSchedules.length > 0 && (
              <div className={styles.row}>
                <div className="col-12">
                  <h3>Special Sessions</h3>
                </div>
                {dateModalSchedules.map(schedule => {
                  const service = _.find(services, ['id', schedule.plan_id]);
                  const option = _.find(service.options, option =>
                    _.find(option.choices, ['schedule_id', schedule.id]),
                  );
                  const choice = _.find(option.choices, [
                    'schedule_id',
                    schedule.id,
                  ]);
                  return (
                    <div key={schedule.id} className="col-12">
                      <div className={styles.serviceSchedule}>
                        <PlanCard
                          plan={service}
                          detailsButtonType="link"
                          linkLearningCenter={learningCenterId}
                          linkPrefix="/services"
                        />
                        <div className="d-flex flex-column py-3">
                          <OptionChoiceCard choice={choice} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {dateModalAvailabilities && dateModalAvailabilities.length > 0 && (
              <div className={styles.row}>
                <div className="col-12 mt-3">
                  <h3>Available for scheduling</h3>
                </div>
                <div className="col-12 d-flex flex-wrap justify-content-between">
                  {dateModalAvailabilities.map(availability => (
                    <div
                      key={availability.start}
                      className={styles.availabilityTime}
                    >
                      <span>
                        From {availability.start} to {availability.end}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default LearningCenterDetails;
